import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Editor from '@monaco-editor/react';
import { useQuery } from '@tanstack/react-query';
import { useState, useRef, useMemo, useCallback, useEffect, useContext } from 'react';
import { Button, Table, useLocalizeMessage, useToastNotification, SegmentedControl, Select, } from 'libs.nucleus';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { StudiesContext } from '../../../contexts';
export const AddEditMeasureStep2 = ({ formData, onFormChange, dataView, isEditMode = false, onQueryValidationChange, }) => {
    const translate = useLocalizeMessage();
    const toast = useToastNotification();
    const { entityId } = useContext(AuthContext);
    const { studies } = useContext(StudiesContext);
    const webClient = useApiClient(ApiClientService.WEB);
    const momClient = useApiClient(ApiClientService.MOM);
    const editorRef = useRef(null);
    const monacoRef = useRef(null);
    const [useSqlEditor, setUseSqlEditor] = useState(formData.calculationType === 'SQL');
    const [sqlQuery, setSqlQuery] = useState(formData.sql || '');
    const [queryResult, setQueryResult] = useState([]);
    const [isRunningQuery, setIsRunningQuery] = useState(false);
    const [measureFunction, setMeasureFunction] = useState(formData.measureFunction || 'Count');
    const [countDimension, setCountDimension] = useState(formData.dimension || '');
    const [hasRunQuery, setHasRunQuery] = useState(false);
    // Segmented control items
    const segmentedControlItems = useMemo(() => [
        { label: translate('Form'), id: 'form' },
        { label: translate('Expression'), id: 'expression' },
    ], [translate]);
    const handleSegmentChange = useCallback((_item, index) => {
        const isExpression = index === 1;
        setUseSqlEditor(isExpression);
        // Reset query state when switching modes
        setSqlQuery('');
        setQueryResult([]);
        setHasRunQuery(false);
    }, []);
    // Notify parent about query validation state
    useEffect(() => {
        const isValid = hasRunQuery && queryResult.length > 0;
        onQueryValidationChange?.(isValid);
    }, [hasRunQuery, queryResult.length, onQueryValidationChange]);
    // Local state for study and environment selection (for preview only)
    const [selectedStudy, setSelectedStudy] = useState(undefined);
    const [selectedEnvironment, setSelectedEnvironment] = useState(undefined);
    const studyOptions = useMemo(() => studies.map((study) => ({
        label: study.name,
        value: study.id.toString(),
    })), [studies]);
    const fetchEnvironments = useCallback(async () => {
        if (!selectedStudy?.value) {
            return [];
        }
        try {
            const { data: response } = await momClient.get(`/v1/entities/${entityId}/studies/${selectedStudy.value}/environments`, {
                params: {
                    clusterTypes: 'dev',
                    withCortexAccess: true,
                },
            });
            return response.data.filter((env) => !env.deployment.isProd);
        }
        catch (error) {
            return [];
        }
    }, [selectedStudy?.value, entityId, momClient]);
    const { data: environments = [], isLoading: isLoadingEnvironments } = useQuery({
        queryKey: ['entities', entityId, 'studies', selectedStudy?.value, 'environments'],
        queryFn: fetchEnvironments,
        enabled: !!entityId && !!selectedStudy?.value,
        refetchOnWindowFocus: false,
    });
    const environmentOptions = useMemo(() => environments.map((env) => ({
        label: env.name,
        value: env.cortexId || env.id.toString(),
    })), [environments]);
    // Reset environment when study changes
    const handleStudyChange = useCallback((option) => {
        setSelectedStudy(option);
        setSelectedEnvironment(undefined);
    }, []);
    const handleEnvironmentChange = useCallback((option) => {
        setSelectedEnvironment(option);
    }, []);
    // Update state when formData changes (e.g., when editing)
    useEffect(() => {
        setUseSqlEditor(formData.calculationType === 'SQL');
        setSqlQuery(formData.sql || '');
        setMeasureFunction(formData.measureFunction || 'Count');
        setCountDimension(formData.dimension || '');
        // Reset query result when switching between edit/create mode
        if (isEditMode) {
            setQueryResult([]);
        }
    }, [formData.calculationType, formData.sql, formData.measureFunction, formData.dimension, isEditMode]);
    // Get available dimensions for the dropdown
    const dimensionOptions = useMemo(() => {
        if (!dataView.data?.dimensions) {
            return [];
        }
        return Object.entries(dataView.data.dimensions).map(([key, dimension]) => ({
            label: dimension.title,
            value: key,
        }));
    }, [dataView.data?.dimensions]);
    // Measure function options
    const measureFunctionOptions = useMemo(() => [
        { label: translate('Count'), value: 'Count' },
        { label: translate('Sum'), value: 'Sum' },
        { label: translate('Average'), value: 'Average' },
        { label: translate('Min'), value: 'Min' },
        { label: translate('Max'), value: 'Max' },
    ], [translate]);
    useEffect(() => {
        const calculationType = useSqlEditor ? 'SQL' : 'DROPDOWN';
        onFormChange({
            ...formData,
            calculationType,
            sql: useSqlEditor ? sqlQuery : undefined,
            measureFunction: useSqlEditor ? undefined : measureFunction,
            dimension: useSqlEditor ? undefined : countDimension,
        });
    }, [useSqlEditor, sqlQuery, measureFunction, countDimension, formData, onFormChange]);
    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        monacoRef.current = monaco;
    };
    const handleSQLChange = useCallback((value) => {
        const newSql = value || '';
        setSqlQuery(newSql);
        onFormChange({
            ...formData,
            sql: newSql,
        });
    }, [formData, onFormChange]);
    const handleRunQuery = useCallback(async () => {
        if (!sqlQuery.trim()) {
            toast.addNotification({
                type: 'error',
                title: translate('Please enter a SQL query'),
            });
            return;
        }
        if (!selectedStudy?.value || !selectedEnvironment?.value) {
            toast.addNotification({
                type: 'error',
                title: translate('Please select a study and environment to run queries'),
            });
            return;
        }
        setIsRunningQuery(true);
        setQueryResult([]);
        try {
            const baseSql = dataView.data?.sql?.trim();
            if (!baseSql) {
                toast.addNotification({
                    type: 'error',
                    title: translate('Dataset SQL query is missing'),
                    subtitle: translate('The dataset SQL query is required to preview measures. Please ensure dimensions were created with a SQL query.'),
                });
                setIsRunningQuery(false);
                return;
            }
            // Build the measure name from formData or use a default
            const measureName = formData.name ? formData.name.toLowerCase().replace(/\s+/g, '_') : 'custom_measure';
            // Construct SQL following the same pattern as dropdown mode:
            // SELECT <user's sql snippet> as "<measure_name>" FROM (<dataset sql>)
            const sql = `SELECT ${sqlQuery.trim()} as "${measureName}" FROM (${baseSql}) LIMIT 100`;
            const response = await webClient.post(`/api/data_transfer/configs/query`, {
                sql,
                explain: false,
                environmentId: selectedEnvironment.value,
                studyId: selectedStudy.value,
            });
            setQueryResult(response.data || []);
            setHasRunQuery(true);
        }
        catch (error) {
            const axiosError = error;
            toast.addNotification({
                type: 'error',
                title: translate('Error running query'),
                subtitle: axiosError.response?.data?.message || translate('Failed to execute SQL query'),
            });
            setQueryResult([]);
            setHasRunQuery(false);
        }
        finally {
            setIsRunningQuery(false);
        }
    }, [sqlQuery, selectedStudy?.value, selectedEnvironment?.value, webClient, toast, translate]);
    const columns = useMemo(() => {
        return queryResult.length > 0
            ? Object.keys(queryResult[0]).map((column) => ({
                header: column,
                accessorKey: column,
            }))
            : [];
    }, [queryResult]);
    const handleMeasureFunctionChange = (option) => {
        const value = option?.value || '';
        setMeasureFunction(value);
        onFormChange({
            ...formData,
            measureFunction: value,
        });
    };
    const handleDimensionChange = (option) => {
        const value = option?.value || '';
        setCountDimension(value);
        onFormChange({
            ...formData,
            dimension: value,
        });
    };
    const handleRunDropdownQuery = useCallback(async () => {
        if (!measureFunction || !countDimension) {
            toast.addNotification({
                type: 'error',
                title: translate('Please select measure function and dimension'),
            });
            return;
        }
        if (!selectedStudy?.value || !selectedEnvironment?.value) {
            toast.addNotification({
                type: 'error',
                title: translate('Please select a study and environment to run queries'),
            });
            return;
        }
        setIsRunningQuery(true);
        setQueryResult([]);
        try {
            // Build SQL query based on measure function and dimension
            const dimensionSql = dataView.data?.dimensions?.[countDimension]?.sql || countDimension;
            const measureName = `${measureFunction.toLowerCase()}_${countDimension}`;
            const baseSql = dataView.data?.sql?.trim();
            if (!baseSql) {
                toast.addNotification({
                    type: 'error',
                    title: translate('Dataset SQL query is missing'),
                    subtitle: translate('The dataset SQL query is required to preview measures. Please ensure dimensions were created with a SQL query.'),
                });
                setIsRunningQuery(false);
                return;
            }
            // Generate SQL based on measure function
            // Wrap the base SQL query in parentheses to use it as a subquery
            let sql = '';
            switch (measureFunction) {
                case 'Count':
                    sql = `SELECT COUNT(DISTINCT ${dimensionSql}) as "${measureName}" FROM (${baseSql})`;
                    break;
                case 'Sum':
                    sql = `SELECT SUM(${dimensionSql}) as "${measureName}" FROM (${baseSql})`;
                    break;
                case 'Average':
                    sql = `SELECT AVG(${dimensionSql}) as "${measureName}" FROM (${baseSql})`;
                    break;
                case 'Min':
                    sql = `SELECT MIN(${dimensionSql}) as "${measureName}" FROM (${baseSql})`;
                    break;
                case 'Max':
                    sql = `SELECT MAX(${dimensionSql}) as "${measureName}" FROM (${baseSql})`;
                    break;
                default:
                    sql = `SELECT COUNT(DISTINCT ${dimensionSql}) as "${measureName}" FROM (${baseSql})`;
            }
            const response = await webClient.post(`/api/data_transfer/configs/query`, {
                sql: `${sql} LIMIT 100`,
                explain: false,
                environmentId: selectedEnvironment.value,
                studyId: selectedStudy.value,
            });
            setQueryResult(response.data || []);
            setHasRunQuery(true);
        }
        catch (error) {
            const axiosError = error;
            toast.addNotification({
                type: 'error',
                title: translate('Error running query'),
                subtitle: axiosError.response?.data?.message || translate('Failed to execute SQL query'),
            });
            setQueryResult([]);
            setHasRunQuery(false);
        }
        finally {
            setIsRunningQuery(false);
        }
    }, [
        measureFunction,
        countDimension,
        selectedStudy?.value,
        selectedEnvironment?.value,
        webClient,
        toast,
        translate,
        dataView.data,
    ]);
    return (_jsxs("div", { className: 'grid grid-cols-2 gap-4 size-full min-h-[500px]', children: [_jsxs("div", { className: 'space-y-4 size-full flex flex-col min-w-0', children: [_jsxs("div", { className: 'flex items-center justify-between pb-4 border-b', children: [_jsx("div", { children: translate('Editor') }), _jsxs("div", { className: 'flex items-center gap-4', children: [_jsx(Select, { placeholder: translate('Select a study'), options: studyOptions, value: selectedStudy, onChange: handleStudyChange, width: 'full', searchableOptions: true }), _jsx(Select, { placeholder: translate('Select an environment'), options: environmentOptions, value: selectedEnvironment, onChange: handleEnvironmentChange, width: 'full', disabled: !selectedStudy?.value || isLoadingEnvironments, searchableOptions: true }), _jsx(Button, { label: translate('Run'), onClick: useSqlEditor ? handleRunQuery : handleRunDropdownQuery, disabled: isRunningQuery || (useSqlEditor ? !sqlQuery.trim() : !measureFunction || !countDimension), variant: 'primary' })] })] }), _jsx("div", { children: _jsx(SegmentedControl, { items: segmentedControlItems, onItemClick: handleSegmentChange, initialItemIndex: useSqlEditor ? 1 : 0 }) }), useSqlEditor ? (_jsx("div", { className: 'border rounded-lg flex-1 py-4', children: _jsx(Editor, { height: '100%', defaultLanguage: 'sql', value: sqlQuery, onChange: handleSQLChange, onMount: handleEditorDidMount, options: {
                                minimap: { enabled: false },
                                fontSize: 14,
                                formatOnType: true,
                                lineNumbers: 'on',
                                automaticLayout: true,
                            } }) })) : (_jsxs("div", { className: 'flex flex-col gap-6', children: [_jsx(Select, { label: translate('Measure function'), options: measureFunctionOptions, value: measureFunction ? measureFunctionOptions.find((opt) => opt.value === measureFunction) : undefined, onChange: handleMeasureFunctionChange, width: 'full' }), _jsx("div", { className: 'pl-6', children: _jsx(Select, { label: translate('{measureFunction} dimension', {
                                        measureFunction: measureFunctionOptions.find((opt) => opt.value === measureFunction)?.label || measureFunction,
                                    }), options: dimensionOptions, value: countDimension ? dimensionOptions.find((opt) => opt.value === countDimension) : undefined, onChange: handleDimensionChange, width: 'full' }) })] }))] }), _jsxs("div", { className: 'border-l px-4 space-y-4 min-w-0 overflow-hidden flex flex-col', children: [_jsx("div", { className: 'text-md font-semibold', children: translate('Data preview') }), _jsx("div", { className: 'overflow-auto flex-1', children: queryResult.length > 0 || isRunningQuery ? (_jsx(Table, { columns: columns, data: queryResult, isLoading: isRunningQuery })) : (_jsx("div", { className: 'flex items-center justify-center h-full', children: _jsx("div", { className: 'text-center text-text-secondary', children: useSqlEditor
                                    ? translate('Enter a measure expression (e.g., SUM(amount), COUNT(DISTINCT id)), then click Run to preview.')
                                    : translate('Select measure function and dimension, then click Run to preview data.') }) })) })] })] }));
};
